import axios, { AxiosResponse } from 'axios';
import {
  User,
  OpportunityResponse,
  LoginCredentials,
  SignupData,
  PremiumPreference,
  UpdateProfilePayload,
  AdminStats,
  AdminUsersResponse,
  AdminUserMetricPoint,
  AdminContractMetricPoint,
  LandingStats,
  AdminIndustryOverrideRow,
  AdminIndustryReviewStatus,
  AdminBillingOptions,
  OpportunityDetail,
  BlogPost,
  BlogPostListResponse,
  BlogPostPayload,
  BlogAsset,
} from '../types';

type FiltersApiResponse = {
  industries: string[];
  opportunity_types?: string[];
  procuring_types?: string[];
  procuring_organizations?: string[];
  cities?: string[];
  statuses: string[];
};

type IndustryOverrideOptionsResponse = {
  procuring_organizations: string[];
  statuses: string[];
};

const normalizeBaseUrl = (value: string) => value.replace(/\/+$/, '');

// Production stays same-origin. Local dev targets the Flask app on port 5002
// unless explicitly overridden for a different backend environment.
const DEV_API_URL = normalizeBaseUrl(import.meta.env.VITE_API_URL || 'http://localhost:5002');
const API_BASE_URL = import.meta.env.PROD ? '' : DEV_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // For session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth headers if needed
api.interceptors.request.use((config) => {
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Do not hard-redirect on 401; let the app's auth gate handle it
    return Promise.reject(error);
  }
);

export const buildBackendUrl = (path: string) => {
  if (!path.startsWith('/')) {
    throw new Error(`Backend path must start with '/': ${path}`);
  }

  return import.meta.env.PROD ? path : `${DEV_API_URL}${path}`;
};

export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse> => {
    return api.post('/login', credentials);
  },

  signup: (data: SignupData): Promise<AxiosResponse> => {
    return api.post('/signup', data);
  },

  logout: (): Promise<AxiosResponse> => {
    return api.post('/logout');
  },

  getProfile: (): Promise<AxiosResponse<{ user: User; preferences: any; premium_preferences: PremiumPreference[]; business_types: string[] }>> => {
    return api.get('/api/user/profile');
  },

  updateProfile: (payload: UpdateProfilePayload): Promise<AxiosResponse<{ user: User; preferences: any; premium_preferences: PremiumPreference[]; business_types: string[] }>> => {
    return api.put('/api/user/profile', payload);
  },

  requestPasswordReset: (email: string): Promise<AxiosResponse<{ success: boolean }>> => {
    return api.post('/api/password/reset-request', { email });
  },

  confirmPasswordReset: (token: string, new_password: string, confirm_password: string): Promise<AxiosResponse<{ success: boolean; error?: string }>> => {
    return api.post('/api/password/reset-confirm', { token, new_password, confirm_password });
  },

  changePassword: (current_password: string, new_password: string, confirm_password: string): Promise<AxiosResponse<{ success: boolean }>> => {
    return api.post('/api/user/password-change', { current_password, new_password, confirm_password });
  },

  deleteAccount: (payload: { confirm: string; password?: string }): Promise<AxiosResponse<{ success: boolean }>> => {
    return api.post('/api/user/delete', payload);
  },
};

export const opportunityAPI = {
  getOpportunities: (mode: 'browse' | 'feed' = 'browse'): Promise<AxiosResponse<OpportunityResponse>> => {
    return api.get(`/api/opportunities?mode=${mode}`);
  },

  getOpportunityDetail: (id: number): Promise<AxiosResponse<{ success: boolean; opportunity: OpportunityDetail }>> => {
    return api.get(`/api/opportunities/${id}`);
  },

  getLiveFilters: (): Promise<AxiosResponse<FiltersApiResponse>> => {
    return api.get('/api/filters/live');
  },

  getCanonicalFilters: (): Promise<AxiosResponse<FiltersApiResponse>> => {
    return api.get('/api/filters/canonical');
  },

  getPremiumPreferences: (): Promise<AxiosResponse<{ success: boolean; preferences: { cities?: string[]; procuring_organizations?: string[]; procuring_types?: string[]; industries: string[]; keywords: string[] }; notification_frequency?: string }>> => {
    return api.get('/api/preferences/premium');
  },

  savePremiumPreferences: (payload: { preferences: { cities: string[]; procuring_types: string[]; industries: string[]; keywords: string[] }; notification_frequency?: string }): Promise<AxiosResponse> => {
    return api.post('/api/preferences/premium', payload);
  },
};

export const savedOpportunityAPI = {
  list: (): Promise<AxiosResponse<{ success: boolean; opportunities: OpportunityDetail[]; total: number }>> => {
    return api.get('/api/saved-opportunities');
  },

  save: (contractId: number): Promise<AxiosResponse<{ success: boolean; contract_id: number; is_saved: true; saved_at: string }>> => {
    return api.put(`/api/saved-opportunities/${contractId}`);
  },

  unsave: (contractId: number): Promise<AxiosResponse<{ success: boolean; contract_id: number; is_saved: false }>> => {
    return api.delete(`/api/saved-opportunities/${contractId}`);
  },
};

export const stripeAPI = {
  createCheckoutSession: (): Promise<AxiosResponse<{ success: boolean; checkout_url: string; session_id: string }>> => {
    return api.post('/api/stripe/create-checkout-session');
  },

  confirmCheckout: (sessionId: string): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
    return api.post('/api/stripe/checkout-success', { session_id: sessionId });
  },

  cancelSubscription: (): Promise<AxiosResponse<{ success: boolean; message: string; cancels_on?: string }>> => {
    return api.post('/api/stripe/cancel-subscription');
  },

  createBillingPortalSession: (): Promise<AxiosResponse<{ success: boolean; url: string }>> => {
    return api.post('/api/stripe/billing-portal');
  },

  syncSubscription: (): Promise<AxiosResponse<{ success: boolean; status?: string }>> => {
    return api.post('/api/stripe/sync-subscription');
  },
};

export const adminAPI = {
  getStats: (): Promise<AxiosResponse<AdminStats>> => {
    return api.get('/api/admin/stats');
  },

  getUsers: (params: { page?: number; pageSize?: number; search?: string } = {}): Promise<AxiosResponse<AdminUsersResponse>> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('page_size', params.pageSize.toString());
    if (params.search) query.set('search', params.search);
    const qs = query.toString();
    return api.get(`/api/admin/users${qs ? `?${qs}` : ''}`);
  },

  getUserMetrics: (): Promise<AxiosResponse<{ points: AdminUserMetricPoint[] }>> => {
    return api.get('/api/admin/user-metrics');
  },

  getContractMetrics: (): Promise<AxiosResponse<{ points: AdminContractMetricPoint[] }>> => {
    return api.get('/api/admin/contract-metrics');
  },

  unlock: (passcode: string): Promise<AxiosResponse<{ success: boolean; required: boolean }>> => {
    return api.post('/api/admin/gate', { passcode });
  },

  getIndustryTaxonomy: (): Promise<AxiosResponse<{ taxonomy: string[] }>> => {
    return api.get('/api/admin/industry_overrides/taxonomy');
  },

  getIndustryOverrideOptions: (params: {
    includeClosed?: boolean;
  } = {}): Promise<AxiosResponse<IndustryOverrideOptionsResponse>> => {
    const query = new URLSearchParams();
    if (params.includeClosed !== undefined) {
      query.set('include_closed', params.includeClosed ? 'true' : 'false');
    }
    const qs = query.toString();
    return api.get(`/api/admin/industry_overrides/options${qs ? `?${qs}` : ''}`);
  },

  getIndustryOverrides: (params: {
    city?: string[];
    status?: string[];
    industry?: string[];
    hasOverride?: boolean;
    includeClosed?: boolean;
    minDue?: string;
    maxDue?: string;
    sort?: string;
    direction?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    query?: string;
    sinceLastReview?: boolean;
  } = {}): Promise<AxiosResponse<{ contracts: AdminIndustryOverrideRow[]; page: number; pageSize: number; total: number }>> => {
    const query = new URLSearchParams();
    if (params.city) params.city.forEach((value) => query.append('city', value));
    if (params.status) params.status.forEach((value) => query.append('status', value));
    if (params.industry) params.industry.forEach((value) => query.append('industry', value));
    if (params.hasOverride !== undefined) query.set('has_override', params.hasOverride ? 'true' : 'false');
    if (params.includeClosed) query.set('include_closed', 'true');
    if (params.minDue) query.set('min_due', params.minDue);
    if (params.maxDue) query.set('max_due', params.maxDue);
    if (params.sort) query.set('sort', params.sort);
    if (params.direction) query.set('direction', params.direction);
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('page_size', params.pageSize.toString());
    if (params.query) query.set('q', params.query);
    if (params.sinceLastReview) query.set('since_last_review', 'true');
    const qs = query.toString();
    return api.get(`/api/admin/industry_overrides/contracts${qs ? `?${qs}` : ''}`);
  },

  downloadIndustryOverridesCsv: (params: {
    city?: string[];
    status?: string[];
    industry?: string[];
    hasOverride?: boolean;
    includeClosed?: boolean;
    minDue?: string;
    maxDue?: string;
    sort?: string;
    direction?: 'asc' | 'desc';
    query?: string;
  } = {}): Promise<AxiosResponse<Blob>> => {
    const query = new URLSearchParams();
    query.set('format', 'csv');
    if (params.city) params.city.forEach((value) => query.append('city', value));
    if (params.status) params.status.forEach((value) => query.append('status', value));
    if (params.industry) params.industry.forEach((value) => query.append('industry', value));
    if (params.hasOverride !== undefined) query.set('has_override', params.hasOverride ? 'true' : 'false');
    if (params.includeClosed) query.set('include_closed', 'true');
    if (params.minDue) query.set('min_due', params.minDue);
    if (params.maxDue) query.set('max_due', params.maxDue);
    if (params.sort) query.set('sort', params.sort);
    if (params.direction) query.set('direction', params.direction);
    if (params.query) query.set('q', params.query);
    const qs = query.toString();
    return api.get(`/api/admin/industry_overrides/contracts?${qs}`, {
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    });
  },

  updateIndustryOverride: (payload: {
    contractId: string;
    industry?: string | null;
    note?: string | null;
  }): Promise<AxiosResponse<{ contract: AdminIndustryOverrideRow }>> => {
    return api.patch('/api/admin/industry_overrides/contracts', payload);
  },

  getIndustryReviewStatus: (): Promise<AxiosResponse<AdminIndustryReviewStatus>> => {
    return api.get('/api/admin/industry_overrides/review-status');
  },

  markIndustryReviewCheckpoint: (): Promise<AxiosResponse<AdminIndustryReviewStatus>> => {
    return api.post('/api/admin/industry_overrides/review-status');
  },
};

export const adminBillingAPI = {
  getOptions: (userId: number): Promise<AxiosResponse<AdminBillingOptions>> => {
    return api.get(`/api/admin/billing/options?user_id=${userId}`);
  },

  createPortalSession: (userId: number): Promise<AxiosResponse<{ success: boolean; url: string }>> => {
    return api.post('/api/admin/billing/portal', { user_id: userId });
  },

  grantAccess: (payload: { user_id: number; action_id: string }): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
    return api.post('/api/admin/billing/grant', payload);
  },
};

export const publicAPI = {
  getLandingStats: (): Promise<AxiosResponse<LandingStats>> => {
    return api.get('/api/public/landing-stats');
  },
  getLegalContent: (slug: 'terms' | 'privacy'): Promise<AxiosResponse<{ content: string }>> => {
    return api.get(`/api/public/legal/${slug}`);
  },
  getInsights: (): Promise<AxiosResponse> => {
    return api.get('/api/public/insights');
  },
};

export const blogAPI = {
  listPublicPosts: (params: {
    page?: number;
    pageSize?: number;
    query?: string;
    tag?: string;
  } = {}): Promise<AxiosResponse<BlogPostListResponse>> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('page_size', params.pageSize.toString());
    if (params.query) query.set('q', params.query);
    if (params.tag) query.set('tag', params.tag);
    const qs = query.toString();
    return api.get(`/api/public/blog/posts${qs ? `?${qs}` : ''}`);
  },

  getPublicPost: (slug: string): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.get(`/api/public/blog/posts/${slug}`);
  },

  listAdminPosts: (params: {
    page?: number;
    pageSize?: number;
    query?: string;
    status?: 'draft' | 'published' | 'all';
  } = {}): Promise<AxiosResponse<BlogPostListResponse>> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page.toString());
    if (params.pageSize) query.set('page_size', params.pageSize.toString());
    if (params.query) query.set('q', params.query);
    if (params.status && params.status !== 'all') query.set('status', params.status);
    const qs = query.toString();
    return api.get(`/api/admin/blog/posts${qs ? `?${qs}` : ''}`);
  },

  getAdminPost: (postId: number): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.get(`/api/admin/blog/posts/${postId}`);
  },

  createPost: (payload: BlogPostPayload): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.post('/api/admin/blog/posts', payload);
  },

  updatePost: (postId: number, payload: BlogPostPayload): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.put(`/api/admin/blog/posts/${postId}`, payload);
  },

  publishPost: (postId: number): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.post(`/api/admin/blog/posts/${postId}/publish`);
  },

  unpublishPost: (postId: number): Promise<AxiosResponse<{ post: BlogPost }>> => {
    return api.post(`/api/admin/blog/posts/${postId}/unpublish`);
  },

  uploadAsset: (file: File): Promise<AxiosResponse<{ asset: BlogAsset }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/admin/blog/assets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Standalone API methods (not grouped)
export const getDetailedInsights = (queryString: string): Promise<AxiosResponse> => {
  return api.get(`/api/insights/detailed${queryString ? `?${queryString}` : ''}`);
};

export default api;
